import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { COL, type Reel } from '@eprintsmart/shared';
import { db, storage } from '../firebase';

/** CRUD for shoppable home-page reels. Videos and posters live in Storage under
 * reels/{id}/; the doc keeps their public download URLs. */

const reels = () => collection(db, COL.reels);

export function watchReels(cb: (rows: Reel[]) => void): () => void {
  return onSnapshot(query(reels(), orderBy('order')), (snap) =>
    cb(snap.docs.map((d) => ({ ...(d.data() as Reel), id: d.id }))),
  );
}

export async function uploadReelFile(reelId: string, kind: 'video' | 'poster', file: File): Promise<string> {
  const ext = file.name.split('.').pop() || (kind === 'video' ? 'mp4' : 'jpg');
  const r = ref(storage, `reels/${reelId}/${kind}.${ext}`);
  await uploadBytes(r, file, { contentType: file.type });
  return getDownloadURL(r);
}

export async function createReel(input: {
  label: string;
  productSlug?: string;
  order: number;
  videoFile: File;
  posterFile?: File;
}): Promise<void> {
  const id = doc(reels()).id;
  const now = Date.now();
  const videoUrl = await uploadReelFile(id, 'video', input.videoFile);
  const posterUrl = input.posterFile ? await uploadReelFile(id, 'poster', input.posterFile) : undefined;
  await setDoc(doc(reels(), id), {
    id,
    label: input.label,
    ...(input.productSlug ? { productSlug: input.productSlug } : {}),
    videoUrl,
    ...(posterUrl ? { posterUrl } : {}),
    order: input.order,
    active: true,
    createdAt: now,
    updatedAt: now,
  });
}

export async function updateReel(id: string, patch: Partial<Reel>): Promise<void> {
  await updateDoc(doc(reels(), id), { ...patch, updatedAt: Date.now() });
}

export async function deleteReel(id: string): Promise<void> {
  await deleteDoc(doc(reels(), id));
}
